import { IconClose, IconPin, IconSearch } from './Icons.jsx'

/**
 * The search field at the top of the panel. Shows the picked place when not
 * searching; tapping it starts a new search. Esc / "Zrušit" leave search, and
 * so does leaving the field with nothing typed.
 */
export function SearchField({
  t,
  inputRef,
  query,
  setQuery,
  active,
  onActivate,
  onCancel,
  onLeave,
  onSubmit,
  pinLabel,
  onClearPin,
  showCancel,
  shortcutHint,
}) {
  const shown = active ? query : pinLabel || ''
  return (
    <form
      className={`search ${active ? 'is-active' : ''}`}
      role="search"
      onSubmit={(e) => {
        e.preventDefault()
        onSubmit?.()
      }}
    >
      <div className="search__box">
        <IconSearch className="search__icon" />
        <input
          ref={inputRef}
          type="search"
          className="search__input"
          placeholder={t('searchPlaceholder')}
          aria-label={t('searchPlaceholder')}
          value={shown}
          enterKeyHint="search"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          onFocus={onActivate}
          onBlur={onLeave}
          onChange={(e) => {
            if (!active) onActivate?.()
            setQuery(e.target.value)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault()
              onCancel?.()
            }
          }}
        />
        {shown ? (
          <button
            type="button"
            className="search__clear"
            aria-label={t('clearSearch')}
            onClick={() => {
              if (!active) return onClearPin?.()
              setQuery('')
              inputRef.current?.focus() // straight back to typing
            }}
          >
            <IconClose />
          </button>
        ) : (
          shortcutHint && (
            <kbd className="search__kbd" title={t('searchShortcut')}>
              /
            </kbd>
          )
        )}
      </div>
      {active && showCancel && (
        <button type="button" className="search__cancel" onClick={onCancel}>
          {t('searchCancel')}
        </button>
      )}
    </form>
  )
}

/** Results list with honest loading / empty / error states. */
export function SearchResults({ t, geo, onPick }) {
  const { status, results } = geo
  return (
    <div className="results">
      <p className="sr-only" aria-live="polite">
        {status === 'done' ? t('searchResults', results.length) : ''}
      </p>
      {status === 'short' && <p className="results__note">{t('searchShort')}</p>}
      {status === 'loading' && results.length === 0 && (
        <p className="results__note">
          <span className="spinner spinner--sm" aria-hidden="true" /> {t('searching')}
        </p>
      )}
      {status === 'error' && <p className="results__note is-bad">{t('searchFailed')}</p>}
      {status === 'done' && results.length === 0 && <p className="results__note">{t('searchNoResults')}</p>}
      {results.length > 0 && (
        <ul className={`results__list ${status === 'loading' ? 'is-stale' : ''}`}>
          {results.map((r, i) => (
            <li key={`${r.name}|${r.detail}|${i}`}>
              <button type="button" className="result" onClick={() => onPick(r)}>
                <span className="result__icon" aria-hidden="true">
                  <IconPin />
                </span>
                <span className="result__text">
                  <span className="result__name">{r.name}</span>
                  {r.detail && <span className="result__detail">{r.detail}</span>}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
